import React, { useState, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Route, Switch, Link } from 'react-router-dom';
import { i18nIntl, calculateSliceSize, formatNumber, generateRouletteCanvas, preventSelection, getLayout, RGB_2_ARRAY, isNotEmpty, isDesktop } from '../../lib/ep.js';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import ColorPicker from '../builder/ColorPicker';
import FloatingLayer from '../builder/FloatingLayer';
import Input from '../builder/Input';
import { AutoPostImageUploader } from '../builder/ImageUploader';
import Message, { AlertMessage } from '../builder/Message';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range';
import SaveButton from '../builder/SaveButton';
import SectionSubtitle from '../builder/SectionSubtitle';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';
import TextArea from '../builder/TextArea';

import ContainedRoulette from '../front/ContainedRoulette';

import RouletteSliceTemplateLayer from '../games/RouletteSliceTemplateLayer';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import Checkbox from '../builder/Checkbox.js';
import Tooltip from '../builder/Tooltip.js';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
const TextAreaPlaceHolder = injectIntl(transferPlaceholder(TextArea));

const DragHandle = SortableHandle(() => <span class="sorter_target"></span>);

const SortableFields = SortableContainer(({ items, ...props }) => {
	const roulette_data = props.roulette_data;
	const onlyRoulette = props.onlyRoulette;
	const no_prize_color = props.no_prize_color;
	const no_prize_label = props.no_prize_label;

	return (
		<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap">
			{items.map((slice, index) => {
				const prize_data = roulette_data[slice.prize];
				const color = (prize_data != undefined && prize_data.color != undefined) ? prize_data.color : ((slice.prize == 0) ? no_prize_color : '#FF0000');
				const slice_name = (slice.prize != 0) ? onlyRoulette.find((obj) => { return obj.id == slice.prize }).title : no_prize_label;

				return (
					<SortableItem key={`slice_${index}`} index={index} slice_name={slice_name} color={color} />
				)
			})}
		</ul>
	);
});

const SortableItem = SortableElement(({ slice_name, color }) => {
	const color_arr = RGB_2_ARRAY(color);
	const styles = { '--ep-text-blue-color':`${color_arr[0]}, ${color_arr[1]}, ${color_arr[2]}` };

	return (
		<li class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
			<div class="item_header">
				<DragHandle/>

				<div class="item_title_container">
					<ActionLink
						_class={{other:'item_title cutTextOnOverflow'}}
						autotranslate={false}
					><span class="gui_colored_icon gui_small_icon ep-circle" style={styles} /><strong>{slice_name}</strong></ActionLink>
				</div>
			</div>
		</li>
	)
});


export const OddsLayer = ({max_odds_summ,close,stage,has_no_prize_slices,has_prizes_iw,change,prizes,...props}) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [roulette_odds, setRouletteOdds] = useState({...stage.roulette_odds} || {});
	const totalOdds = () => {
		let total = 0;
		let has_error = false;
		for(var i in roulette_odds) {
			if(roulette_odds[i] == "") continue;
			let prize_odds = parseInt(roulette_odds[i]);
			if(!isNaN(prize_odds) && prize_odds == roulette_odds[i]) {
				total += prize_odds;
			} else {
				has_error = true;
			}
		}

		return (!has_error) ? total : "ERROR";
	}
	
	const changeOdds = (k, v) => {
		roulette_odds[k] = v;
		setRouletteOdds({...roulette_odds});
	}

	const saveOdds = (e, cb) => {
		change('roulette_odds',roulette_odds);
		props.on_save(() => {
			close();
			cb();
		});
	}

	const disableOdds = (e, cb) => {
		change('roulette_odds',"");
		setRouletteOdds({})
		props.on_save(() => {
			close();
			cb();
		});
	}

	const total_odds = totalOdds();

	return (
		<FloatingLayer _class={{size:'small'}} backButton={close}>
			<div>
				<SectionTitle>Configure prize odds</SectionTitle>
				<SectionSubtitle autotranslate={false}><FormattedMessage id="From this section, you can configure the probability of each of the prizes. The probability can range from 0 to {max}, with {max} being the highest probability that a prize will be awarded." values={{max:max_odds_summ}} /></SectionSubtitle>
				<SectionSubtitle autotranslate={false}><strong><FormattedMessage id="The total number of odds must sum {max}." values={{max:max_odds_summ}} /></strong></SectionSubtitle>

				{(total_odds != "ERROR") ? (
					<Message
						type={((total_odds > 0) ? ((total_odds != max_odds_summ) ? 'error' : 'success') : null)}
						icon={((total_odds > 0) ? ((total_odds != max_odds_summ) ? 'ep-delete-circle redIcon' : 'ep-check-list-item greenIcon') : false)}
						_class={{ other:'in_top' }}
					>
						<p>
							<FormattedMessage id="Total odds: {total}/{max}" values={{total:total_odds, max:max_odds_summ}} />
							{(total_odds > 0 && total_odds < max_odds_summ) && (
								<span class="gui_not_in_start_small">(<FormattedMessage id="{missing} remaining" values={{missing:(max_odds_summ - total_odds)}} />)</span>
							)}
							{(total_odds > 0 && total_odds > max_odds_summ) && (
								<span class="gui_not_in_start_small">(<FormattedMessage id="{exceeds} exceeding" values={{exceeds:(total_odds - max_odds_summ)}} />)</span>
							)}
						</p>
					</Message>
				) : (
					<AlertMessage
						_class={{ other:'in_top' }}
					>
						<p>{i18n("Only integer numeric values are accepted")}</p>
					</AlertMessage>
				)}

				<div class="prize_odds_setter">
					<Model model={roulette_odds} change={changeOdds}>
						{prizes.filter(p=>{
							return p.assignation == '5' && (p.stage_id == null || p.stage_id == stage.id);
						}).map((prize, p) => {
							const _v = (roulette_odds[prize.id] != undefined) ? roulette_odds[prize.id] : 0;

							return (
								<Input key={prize.id} _class={{size:60}} name={prize.id} placeholder={Math.round(max_odds_summ / (prizes.length + ((has_no_prize_slices) ? 1 : 0)))} value={_v}>{prize.title}</Input>
							)
						})}

						{(has_no_prize_slices) && (
							<Input _class={{size:60}} name={0} value={(roulette_odds[0] != undefined) ? roulette_odds[0] : 0}>No prize</Input>
						)}
					</Model>

					{has_prizes_iw && prizes.filter((p) => {
						return p.assignation == '4' && (p.stage_id == null || p.stage_id == stage.id);
					}).map((prize, p) => {
						return (
							<Input
								key={prize.id}
								_class={{size:60}}
								disabled={true}
								name={prize.id}
								value={i18n("Defined by Instant Win")}
								label_props={{
									mandatory:true,
								}}
							>{prize.title}</Input>
						)
					})}
				</div>

				{(has_prizes_iw) && (
					<p class="not_in_top" style={{lineHeight:'1.2em'}}>[*]: {i18n("It is not possible to configure the probability for Instant Win prizes, as these prizes are awarded according to the time of participation of the user.")} <FormattedMessage id="{link} to schedule the allocation date and time for these prizes." values={{link:<ActionLink>Go to Instant Win tool</ActionLink>}} /></p>
				)}

				<ButtonCollection in_floating={true}>
					<SaveButton
						disabled={total_odds != max_odds_summ || total_odds == "ERROR"}
						onClick={saveOdds}
					>Save</SaveButton>

					{(stage.roulette_odds != undefined) && (
						<SaveButton
							type="link"
							onClick={disableOdds}
						>Reset odds</SaveButton>
					)}
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
}

const RouletteBuilder = ({stage, change, ...props}) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ open_prize, setOpenPrize ] = useState(false);
	//const [ display_change_type_layer, setDisplayChangeTypeLayer ] = useState(false);
	const [ display_slice_template_layer, setDisplaySliceTemplateLayer ] = useState(false);
	const [ slices_order, setSlicesOrder ] = useState([]);
	const [ odds_layer, setOddsLayer ] = useState(false);
	const [ slice_image_size, setSliceImageSize ] = useState({ width:300, height:300 });
	const [ num_slices_real, setNumSlicesReal ] = useState(stage.roulette_slices);

	const roulette_canvas = useRef();
	const layout_props = getLayout(props.promotion);

	const toggleOpen = (prize) => {
		setOpenPrize((prize === open_prize) ? false : prize);
	};

	const changePromo = (k, v) => {
		props.promotion[k] = v;
	};

	/**
	 * @deprecated 2025-06-04
	 */
	// const switchRouletteMode = (mode, cb) => {
	// 	change('roulette_type', mode);
	// 	$.post(`/promotions/switchroulettetype/${stage.promotion_id}`, { type:mode });

	// 	if(mode == 1) {
	// 		change('roulette_no_prize', 0);
	// 		props.instantStore.dispatch({type:'DELETEALL'});
	// 		prizes.filter(p => (p.assignation == '4')).map((prize_type) => {
	// 			props.winnerStore.dispatch({type:'CHANGE', field:'assignation', value:'5', id:prize_type.id});
	// 		});
	// 	}

	// 	if(mode == 2) {
	// 		change('roulette_slices', stage.roulette_slices);
	// 		prizes.filter(p => (p.assignation == '5')).map((prize_type) => {
	// 			props.winnerStore.dispatch({type:'CHANGE', field:'assignation', value:'4', id:prize_type.id});
	// 			props.winnerStore.dispatch({type:'CHANGE', field:'conditions', value:'', id:prize_type.id});
	// 		});
	// 	}

	// 	if(cb != undefined) cb();
	// 	setDisplayChangeTypeLayer(false);
	// 	props.on_save();
	// }

	let onlyRoulette = [];

	const prizes = props.winnerStore.getState().prizes.prizes;
	const instants = props.instantStore.getState().instant.instants;

	let instantCount = {};
	instants.map((instant) => {
		instantCount[instant.prize_type] = (instantCount[instant.prize_type] != undefined) ? instantCount[instant.prize_type] + 1 : 1;
	});

	if(stage.roulette_type == undefined) stage.roulette_type = 1;
	let odds_prizes = 0;
	onlyRoulette = prizes.filter((p) => {
		let valid = true;
		if(p.assignation == '5') odds_prizes++;
		if(p.assignation != '5' && p.assignation != '4') valid = false;
		if(p.stage_id != null && p.stage_id != stage.id) valid = false;

		return valid;
	});

	const min_slices = onlyRoulette.length + parseInt(stage.roulette_no_prize);

	const max_slices = 30; //Math.min((stage.roulette_slices - stage.prizes.length - ((stage.roulette_try_again) ? 1 : 0)), 30);

	const no_prize_color = stage?.roulette_no_prize_color ?? 'rgba(0, 0, 0, 1)';
	const no_prize_color_arr = RGB_2_ARRAY(no_prize_color);
	const no_prize_label = stage?.roulette_no_prize_label ?? "No Prize";

	let roulette_data = (stage.roulette_data != undefined) ? stage.roulette_data : {};
	let changed = false;
	let prizeids = {};
	for(var i in onlyRoulette) {
		prizeids[onlyRoulette[i].id] = 1;
		if(roulette_data[onlyRoulette[i].id] == undefined) {
			let hue_changed = 45 * (i - Math.floor(onlyRoulette.length / 2));
			let auto_color = changeHue(layout_props.color.main_color, hue_changed);
			var data = {};
			data['name'] = onlyRoulette[i].title;
			data['color'] = auto_color;
			roulette_data[onlyRoulette[i].id] = data;
			changed = true;
		}
	}

	for(var j in roulette_data) {
		if(prizeids[j] == undefined) {
			delete roulette_data[j];
			changed = true;
		}
	}

	if(changed) change('roulette_data', roulette_data);

	const changeRD = (k, v) => {
		roulette_data[open_prize][k] = v;
		change('roulette_data', roulette_data);
	}

	const changeRDMulti = (values) => {
		roulette_data[open_prize] = {...roulette_data[open_prize],...values};
		change('roulette_data', roulette_data);
	}

	const is_wl = (props.promotion.promo_type == 2) ? true : false;
	const pre_section_main_path = (props.section_main_path != undefined) ? props.section_main_path : '';
	const section_main_path = pre_section_main_path + 'roulette';

	let sections = [
		{ label:"Go back", link:pre_section_main_path + 'options', icon:'knk-go-prev' },
		{ label:"Spin the Wheel", link:section_main_path, icon:'knk-roulette' }
	];

	const slices = Math.max(stage.roulette_slices, min_slices);
	const min_no_prize = (stage.roulette_type == 2) ? 1 : 0;

	const updateNumSlices = (num_slices) => {
		setNumSlicesReal(num_slices);
		setSliceImageSize(calculateSliceSize(300, num_slices));
	};

	if(onlyRoulette.length == 0) {
		return (
			<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true}>
				<NavigationTabsBuilder
					active={'/' + section_main_path}
					sections={sections}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<p>{i18n("Before you can set up and customize the wheel, you must create the prizes that you wish to give away. You must link them to this participation stage, and these will be the prizes that the Prize Wheel will present. You can create prizes based on the probability you define for each prize, or prizes assigned according to the time of participation of the user (Instant Win).")}</p>

				<p><strong>{i18n("Once the prizes have been created, you will be able to access this section again to fully customize the Prize wheel: number of segments, customization of the wheel design and colors, etc.")}</strong></p>

				<ButtonCollection _class={{other:'gui_flex_end not_in_top'}}>
					<ActionButton
						_href="#prizes"
					>Go to create the prizes</ActionButton>
				</ButtonCollection>
			</BuilderMainWithoutSidebar>
		)
	}

	//sections.push({ label:"Download images", link:section_main_path + '/export', icon:'knk-download' });	// DEPRECATED 27-04-2021
	if(is_wl) sections.push({ label:"Advanced", link:`${section_main_path}/advanced`, icon:'knk-script' });

	const parseFontName = (font_name) => {
		if(font_name == undefined) return font_name;
		return font_name.replace(/\+/g, " ");
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Switch>
				<Route exact={true} path={'/' + section_main_path} render={(route) => {
					const has_no_prize_slices = (stage.roulette_no_prize != undefined && stage.roulette_no_prize > 0) ? true : false;
					//const total_odds = (odds_layer) ? totalOdds() : 0;
					const max_odds_summ = 1000;

					let percent_no_prize = 0;
					let odds_no_prize = 0;
					if(stage.roulette_odds != undefined) {
						if(stage.roulette_odds[0] != undefined) odds_no_prize = stage.roulette_odds[0];
					} else {
						percent_no_prize = (stage.roulette_no_prize / stage.roulette_slices * 100).toFixed(0);
					}

					const prizes_iw_count = prizes.filter((obj) => { return obj.assignation == '4'; }).length;
					const has_prizes_iw = (prizes_iw_count > 0) ? true : false;

					const no_prize_has_odds = (prizes_iw_count == prizes.length) ? false : true;

					const font_families = [
						{ k:'secondary', v:`${i18n("***Secondary")} (${parseFontName(layout_props.text.secondary_font)})` },
						{ k:'main', v:`${i18n("Main")} (${parseFontName(layout_props.text.main_font)})` },
						{ k:'generic', v:`${i18n("***Generic")} (${parseFontName("Arial")})` },
					];

					const initial_animations = [
						{ k:'none', v:i18n("Off") },
						{ k:'single', v:i18n("On, 1 light") },
						{ k:'double', v:i18n("On, every 2 lights") },
					];

					return (
						<>
							<BuilderSidebar onSave={props.on_save}>
								<NavigationTabsBuilder
									active={route.location.pathname}
									sections={sections}
								>{props.stage_indicator}</NavigationTabsBuilder>

								<article>
									<BuilderSubSectionTitle
										title="Wheel segments"
										title_actions={<Link to={`roulette/slicesedit`} class="submit_link in_top">{i18n("Edit segments order")}</Link>}
									/>

									<Model model={stage} change={change}>
										<Range min={Math.max(4, min_slices)} max={30} name="roulette_slices" value={slices} default={12} step={1} _class={{size:50}}>Segments</Range>

										<Range min={min_no_prize} max={Math.max(1, max_slices)} name="roulette_no_prize" value={stage.roulette_no_prize} default={1} step={1} _class={{size:50}}>No prize</Range>

										<Select name="roulette_distribution" 
											tooltip={<Tooltip position="top">Define how the system should allocate prizes to segments (wheel slots) when there are more segments than prize types. Uniform: The system will evenly distribute the available prize types across all segments. By Units: The system will prioritize prizes with the highest number of units, assigning them first.</Tooltip>}
											values={[
												{ k:'', v:"By units" },
												{ k:'uniform', v:"Uniform" },
											]}
										>Prize distribution</Select>
									</Model>

									{(!has_no_prize_slices) && (
										<Message
											_class={{ other:'gui_not_in_top_small' }}
										>
											<p>
												{(stage.roulette_type == 2) ? (
													<FormattedMessage id="If the prize assignment is by Instant Win, the best practice is that there are at least {percent} {slot_label} segments in the wheel." values={{percent:'50%', slot_label:'"' + i18n("No prize") + '"'}} />
												) : (
													<FormattedMessage id="You can make the game more exciting by adding {slot_label} segments. If there's a prize for all, leave at 0." values={{slot_label:'"' + i18n("No prize") + '"'}} />
												)}
											</p>
										</Message>
									)}
								</article>

								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Wheel layout options"
										icon='knk-artist-mode'
									/>

									<Model model={stage} change={change}>
										<ColorPicker
											name="roulette_background_color"
											color={stage?.roulette_background_color ?? layout_props.color.background_color}
											enable_reset={(stage.roulette_background_color != layout_props.color.background_color) ? true : false}
											_class={{ size:70 }}
										>Background color</ColorPicker>

										<AutoPostImageUploader
											auto_url={`/promotions/auto_img/${pm.attributes.id}`}
											auto_type='stages'
											name="roulette_custom_bg"
											image={stage.full_roulette_custom_bg}
											small_input={true}
											_size={{ width:1600, height:1200 }}
											display_custom_dragging_instructions="Upload file"
										>Background image</AutoPostImageUploader>

										<AutoPostImageUploader
											auto_url={`/promotions/auto_img/${pm.attributes.id}`}
											auto_type='stages'
											name="roulette_logo"
											image={stage.full_roulette_logo}
											small_input={true}
											_size={{ width:300, height:300 }}
											display_custom_dragging_instructions="Upload file"
										>Logo</AutoPostImageUploader>

										<ColorPicker
											_class={{ size:70 }}
											color={stage?.roulette_ui_color ?? layout_props.color.main_color}
											name="roulette_ui_color"
										>UI color</ColorPicker>

										<Input
											_class={{ size:70 }}
											max_length={16}
											name="roulette_spin_it_btn_label"
											placeholder="Press here"
										>Spin Button</Input>

										{/*<Select
											name="roulette_font_family"
											values={font_families}
											_class={{ size:70 }}
										>Typography</Select>*/}

										<Range
											min={1}
											max={5}
											type="fontsize"
											name="roulette_font_size"
											value={stage?.roulette_font_size ?? 3}
											step={1}
											_class={{ size:50 }}
										>Font size</Range>

										<Checkbox
											name="roulette_hide_final_prize_text"
										>Hide the prize name after spinning the wheel</Checkbox>

										<Checkbox
											name="roulette_zoom_on_spin"
											value={stage?.roulette_zoom_on_spin ?? true}
										>Zoom when spinning</Checkbox>

										<Select
											name="roulette_initial_light_anim"
											values={initial_animations}
											_class={{ size:70 }}
										>Lights</Select>

										<Checkbox
											name='silence'
										>Disable sounds</Checkbox>

										{/*<Checkbox
											name="roulette_variable_strength"
											value={stage?.roulette_variable_strength ?? true}
										>Enable user to chose the strength of the spin</Checkbox>*/}
									</Model>

									<article class="gui_not_in_top_huge">
										<BuilderSubSectionTitle
											title="Prizes in the wheel"
											icon='knk-present'
											bordered={(onlyRoulette.length > 0 || has_no_prize_slices) ? false : null}
											title_actions={(odds_prizes > 0) ? <ActionLink onClick={() => setOddsLayer(true)}>{(stage.roulette_odds != undefined) ? "Edit prize odds" : "Configure prize odds"}</ActionLink> : null}
										/>

										{(onlyRoulette.length > 0 || has_no_prize_slices) && (
											<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap">
												{onlyRoulette.map((prize, p) => {
													const prize_data = roulette_data[prize.id];
													const color = (prize_data != undefined && prize_data.color != undefined) ? prize_data.color : '#D8253B';
													const color_arr = RGB_2_ARRAY(color);
													const styles = { '--ep-text-blue-color':`${color_arr[0]}, ${color_arr[1]}, ${color_arr[2]}` };

													var has_date_conditions = false;
													if(prize.conditions != undefined && prize.conditions.length > 0) {
														var _date_condition = prize.conditions.filter((obj) => { return obj.field == 'date'; } );
														if(_date_condition && _date_condition[0] != undefined) {
															_date_condition = _date_condition[0].value;
															has_date_conditions = EPDateFormat(_date_condition, 'L, HH:mm') + " h";
														}
													}
													const prize_odds = (stage.roulette_odds != undefined && stage.roulette_odds[prize.id] != undefined) ? stage.roulette_odds[prize.id] : 0;

													const with_warning = (instantCount[prize.id] == undefined || instantCount[prize.id] < prize.qty) ? true : false;

													return (
														<li key={p} class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
															<div class={'item_header' + ((open_prize == prize.id) ? ' is_open' : '')}>
																<div class="item_title_container">
																	<ActionLink onClick={() => toggleOpen(prize.id)} _class={{other:'item_title cutTextOnOverflow'}} autotranslate={false}><span class="gui_colored_icon gui_small_icon ep-circle" style={styles} /><strong>{`${prize.title}${(isNotEmpty(prize, 'reference')) ? ` (${prize.reference})` : ''}`}</strong></ActionLink>

																	{(stage.roulette_odds != undefined && prize.assignation == '5') && (
																		<p class="subtitle"><FormattedMessage id="Odds: {odds}/{max} ({percent})%" values={{odds:prize_odds, max:max_odds_summ, percent:formatNumber(100 * prize_odds / max_odds_summ, 2)}} /></p>
																	)}

																	{(prize.assignation == '4' && stage.roulette_type != 2) && (
																		<p class="subtitle">{i18n("Instant Win prize")}</p>
																	)}

																	{(stage.roulette_type == 1 && has_date_conditions !== false) && (
																		<p class="subtitle">
																			<FormattedMessage id="Delivery starts at {datetime}" values={{datetime:has_date_conditions}} />
																		</p>
																	)}

																	{(stage.roulette_type == 2 && prize.assignation == '4') && (
																		<p class={'subtitle' + ((with_warning) ? ' redColor' : '')}>
																			<FormattedMessage id="Instant prizes: {num_prizes} of {total_qty}" values={{num_prizes:((instantCount[prize.id] == undefined) ? 0 : instantCount[prize.id]), total_qty:prize.qty}} />
																		</p>
																	)}
																</div>

																<ButtonCollection>
																	{(prize.assignation == '4') && (
																		<SquaredButton
																			_href="#newinstants"
																			icon='knk-timecounter'
																			tooltip={<Tooltip with_icon={false} position="top">Go to Instant Win tool</Tooltip>}
																			aria-label="Go to Instant Win tool"
																		/>
																	)}

																	{(isNotEmpty(stage,'roulette_odds') && prize_odds == 0 && prize.assignation == '5') && (
																		<SquaredButton
																			onClick={() => setOddsLayer(true)}
																			color="red"
																			icon='knk-alert'
																			tooltip={<Tooltip with_icon={false} position="top">This prize will not be assigned because the defined odds are 0.</Tooltip>}
																			aria-label="Set prize odds"
																		/>
																	)}

																	{(with_warning && prize.assignation == '4') && (
																		<SquaredButton
																			_href="#newinstants"
																			color="red"
																			icon='knk-alert'
																			tooltip={<Tooltip position="top" with_icon={false}>The winning moments have not been scheduled for all units of this prize. Go to the Instant Win tool and assign a moment to each unit.</Tooltip>}
																			aria-label="Edit Instant Win prizes"
																		/>
																	)}

																	<SquaredButton
																		onClick={() => toggleOpen(prize.id)}
																		icon={(open_prize == prize.id) ? 'knk-arrow-t-big' : 'knk-edit'}
																		aria-label="Edit"
																	/>
																</ButtonCollection>
															</div>

															{(open_prize == prize.id) && (
																<div class="gui_item_unfoldable_options">
																	<Model model={roulette_data[prize.id]} change={(k, v) => changeRD(k, v)}>
																		<ColorPicker
																			_class={{size:70}}
																			color={color}
																			name="color"
																		>Color</ColorPicker>
																		
																		<AutoPostImageUploader
																			auto_url={`/promotions/auto_img/${stage.promotion_id}`}
																			auto_type='stages'
																			name="image"
																			changeMulti={changeRDMulti}
																			image={roulette_data[prize.id].full_image}
																			small_input={true}
																			_size={slice_image_size}
																			display_custom_dragging_instructions="Upload file"
																			label_props={{
																				_extra_component:(isDesktop()) ? <ActionLink onClick={() => setDisplaySliceTemplateLayer(true)}>Info</ActionLink> : null,
																			}}
																		>Segment image</AutoPostImageUploader>

																		<AutoPostImageUploader
																			auto_url={`/promotions/auto_img/${stage.promotion_id}`}
																			auto_type='stages'
																			name="given_prize_image"
																			changeMulti={changeRDMulti}
																			image={roulette_data[prize.id].full_given_prize_image}
																			small_input={true}
																			_size={{ width:600, height:600 }}
																			display_custom_dragging_instructions="Upload file"
																			label_props={{
																				tooltip:<Tooltip>The image will appear inside the spinner. If you do not upload any images, the name of the prize will be displayed.</Tooltip>,
																			}}
																		>Final image when winning the prize</AutoPostImageUploader>
																	</Model>

																	<Message
																		_class={{ other:'gui_not_in_top_small in_bottom' }}
																	>
																		<p><FormattedMessage id="{edit_link} (name, units, description, instructions, delivery conditions, etc)" values={{edit_link:<ActionLink _href={'#prizes/edit/' + prize.id + '/options'}>Edit all settings for this prize</ActionLink>}} /></p>
																		{(stage.roulette_type != 1) && (
																			<p>{i18n("Remember that you have to schedule the date and time for each prize unit in the Instant Win tool. If you don't schedule any date and time, the prize will not be given.")} <ActionLink _href="#newinstants">Go to Instant Win tool</ActionLink></p>
																		)}
																	</Message>
																</div>
															)}
														</li>
													)
												})}

												{(has_no_prize_slices) && (
													<li class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
														<div class={'item_header' + ((open_prize === 0) ? ' is_open' : '')}>
															<div class="item_title_container">
																<ActionLink onClick={() => toggleOpen(0)} _class={{other:'item_title cutTextOnOverflow'}} autotranslate={false}>
																	<span class="gui_colored_icon gui_small_icon ep-circle" style={{ '--ep-text-blue-color':`${no_prize_color_arr[0]}, ${no_prize_color_arr[1]}, ${no_prize_color_arr[2]}` }} />
																	<strong>{no_prize_label}</strong>
																</ActionLink>

																<p class="subtitle">
																	{(no_prize_has_odds) ? (
																		<FormattedMessage id="Odds: {odds}/{max} ({percent})%" values={{odds:odds_no_prize, max:max_odds_summ, percent:formatNumber(100 * odds_no_prize / max_odds_summ, 2)}} />
																	) : (
																		<span>{i18n("Depends on Instant Win moments")}</span>
																	)}
																</p>
															</div>

															<ButtonCollection>
																{(isNotEmpty(stage,'roulette_odds') && odds_no_prize == 0) && (
																	<SquaredButton
																		onClick={() => setOddsLayer(true)}
																		color="red"
																		icon='knk-alert'
																		tooltip={<Tooltip with_icon={false} position="top">The user will always receive a prize because the odds to get a ''no prize'' are set to 0.</Tooltip>}
																		aria-label="Set prize odds"
																	/>
																)}

																<SquaredButton
																	onClick={() => toggleOpen(0)}
																	icon='knk-edit'
																	aria-label="Edit"
																/>
															</ButtonCollection>
														</div>

														{(open_prize === 0) && (
															<div class="gui_item_unfoldable_options">
																<Model model={stage} change={change}>
																	<Input name="roulette_no_prize_label" value={no_prize_label} has_emojis={false} _class={{size:70}}>Label</Input>

																	<ColorPicker _class={{size:70}} color={no_prize_color} name="roulette_no_prize_color">Color</ColorPicker>

																	<AutoPostImageUploader
																		auto_url={`/promotions/auto_img/${stage.promotion_id}`}
																		auto_type='stages'
																		name="roulette_no_prize_image"
																		image={stage.full_roulette_no_prize_image}
																		small_input={true}
																		_size={slice_image_size}
																		display_custom_dragging_instructions="Upload file"
																		label_props={{
																			_extra_component:(isDesktop()) ? <ActionLink onClick={() => setDisplaySliceTemplateLayer(true)}>Info</ActionLink> : null,
																		}}
																	>Segment image</AutoPostImageUploader>

																	<AutoPostImageUploader
																		auto_url={`/promotions/auto_img/${stage.promotion_id}`}
																		auto_type='stages'
																		name="given_no_prize_image"
																		image={stage.full_given_no_prize_image}
																		small_input={true}
																		_size={{ width:600, height:600 }}
																		display_custom_dragging_instructions="Upload file"
																		label_props={{
																			tooltip:<Tooltip>The image will appear inside the spinner. If you do not upload any images, the no-prize label will be displayed.</Tooltip>,
																		}}
																	>Final image without prize</AutoPostImageUploader>
																</Model>

																<Message
																	_class={{ other:'gui_not_in_top_small in_bottom' }}
																>
																	<p>{i18n("The indicated probability does not take into account if the prizes are locked at a certain date and time.")}</p>
																</Message>
															</div>
														)}
													</li>
												)}
											</ul>
										)}
									</article>
									
									{(odds_layer) && (
										<OddsLayer
											close={() => setOddsLayer(false)}
											max_odds_summ={max_odds_summ}
											stage={stage}
											intl={props.intl}
											has_no_prize_slices={has_no_prize_slices}
											has_prizes_iw={has_prizes_iw}
											change={change}
											prizes={prizes}
											on_save={props.on_save}
										/>
									)}

									{/* 
									@deprecated 2025-06-04
									{(display_change_type_layer) && (
										<FloatingLayer _class={{size:'small'}} enable_close={false}>
											{(stage.roulette_type == 1) && (
												<div>
													<SectionTitle>Change to Instant Win prize assignment</SectionTitle>
													<SectionSubtitle>A prize is assigned depending on the date and time the administrator has scheduled for each prize unit.</SectionSubtitle>
													<SectionSubtitle autotranslate={false}><strong>{i18n("Note", 'uppercase')}: {i18n("You can't change the type of prize assignment after the promotion has been activated. Use the Test Mode to test how the promotion works while it is still in draft mode.")}</strong></SectionSubtitle>

													<Message
														title="Important"
														_class={{ other:'in_top in_bottom' }}
													>
														<p>{i18n("If you change the prize assignment type, you have to set up the date and time for each prize in the Instant Win tool. If you don't do this, the prize wheel will not give any prizes.")}</p>
													</Message>

													<ButtonCollection in_floating={true}>
														<SaveButton
															onClick={(e, cb) => switchRouletteMode(2, cb)}
														>Yes, I confirm</SaveButton>

														<SecondaryButton
															onClick={() => setDisplayChangeTypeLayer(false)}
														>Cancel</SecondaryButton>
													</ButtonCollection>
												</div>
											)}

											{(stage.roulette_type == 2) && (
												<div>
													<SectionTitle>Change to Probability of the wheel prize assignment</SectionTitle>
													<SectionSubtitle>The probability of winning a prize depends on the number of segments in the wheel.</SectionSubtitle>
													<SectionSubtitle autotranslate={false}><strong>{i18n("Note", 'uppercase')}: {i18n("You can't change the type of prize assignment after the promotion has been activated. Use the Test Mode to test how the promotion works while it is still in draft mode.")}</strong></SectionSubtitle>

													<Message
														title="Important"
														_class={{ other:'in_top in_bottom' }}
													>
														<p>{i18n("If you change the prize assignment type, all instant win moments linked to the prize units will be deleted.")}</p>
													</Message>

													<ButtonCollection in_floating={true}>
														<SaveButton
															onClick={(e, cb) => switchRouletteMode(1, cb)}
														>Yes, I confirm</SaveButton>

														<SecondaryButton
															onClick={() => setDisplayChangeTypeLayer(false)}
														>Cancel</SecondaryButton>
													</ButtonCollection>
												</div>
											)}
										</FloatingLayer>
									)} */}

									{(display_slice_template_layer) && (
										<RouletteSliceTemplateLayer
											close={() => setDisplaySliceTemplateLayer(false)}
											num_slices={num_slices_real}
										/>
									)}
								</article>
							</BuilderSidebar>

							<BuilderCanvas is_adaptable={false} fit_to_window="all">
								<ContainedRoulette
									ref={roulette_canvas}
									prizes={onlyRoulette}
									is_builder={true}
									is_export={false}
									promotion={props.promotion}
									updateNumSlices={updateNumSlices}
									onContinue={() => {}}
									stage={stage}
								/>
							</BuilderCanvas>
						</>
					)
				}} />
				{/* Content Tab - ini */}

				<Route exact={true} path={`/${section_main_path}/slicesedit`} render={(route) => {
					if(slices_order.length == 0) {
						if(stage.roulette_order != undefined && stage.roulette_order != "") {
							setSlicesOrder(stage.roulette_order);
						} else {
							let _slices = [];

							let slices_definition = generateRouletteCanvas(onlyRoulette, {
								slices:stage.roulette_slices,
								no_prize:stage.roulette_no_prize,
							});

							slices_definition.map((slice, s) => {
								_slices.push({ id:s, prize:slice.id });
							});

							setSlicesOrder(_slices);
						}

						return null;
					}

					const onSortEnd = ({ oldIndex, newIndex }) => {
						var order = arrayMove(slices_order, oldIndex, newIndex);

						for(var i in order) {
							order[i].id  = i;
						}

						setSlicesOrder(order);
						change('roulette_order', order);
					};

					const changeRouletteSlices = (k, v) => {
						change(k, v);
						change('roulette_order', null);
						setSlicesOrder([]);
					};

					const saveRouletteOrder = (cb) => {
						stage.roulette_order = slices_order;
						props.on_save(cb);
					}

					return (
						<>
							<BuilderSidebar onSave={saveRouletteOrder}>
								<NavigationTabsBuilder
									active={route.location.pathname}
									sections={[{ label:"Go back", link:section_main_path, icon:'knk-go-prev' }]}
								>{props.stage_indicator}</NavigationTabsBuilder>

								<article>
									<Model model={stage} change={changeRouletteSlices}>
										<Range min={Math.max(4, min_slices)} max={30} name="roulette_slices" value={slices} default={12} step={1} _class={{size:50, first:true}}>Segments</Range>

										<Range min={min_no_prize} max={Math.max(1, max_slices)} name="roulette_no_prize" value={stage.roulette_no_prize} default={1} step={1} _class={{size:50}}>No prize</Range>
									</Model>

									<Message>
										<p>{i18n("From this section, you will be able to modify the order in which the prizes and no-prize segments are displayed in the Wheel.")}</p>
										<p><strong>{i18n("Important: We recommend modifying the order of the segments once you have created all the prizes and when you have configured the number of segments of the Wheel, since in case of modifying the number of segments, you will have to reorder them again.")}</strong></p>
									</Message>
								</article>

								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Manual order of the segments"
										bordered={(slices_order.length > 0) ? false : null}
									/>

									{(slices_order.length > 0) && (
										<SortableFields
											items={slices_order}
											roulette_data={roulette_data}
											onlyRoulette={onlyRoulette}
											no_prize_color={no_prize_color}
											no_prize_label={no_prize_label}
											onSortStart={() => preventSelection(true)}
											onSortEnd={onSortEnd}
											useDragHandle={true}
										/>
									)}
								</article>
							</BuilderSidebar>

							<BuilderCanvas is_adaptable={false} fit_to_window="all">
								<ContainedRoulette
									ref={roulette_canvas}
									prizes={onlyRoulette}
									is_builder={true}
									is_export={false}
									promotion={props.promotion}
									onContinue={() => {}}
									stage={stage}
								/>
							</BuilderCanvas>
						</>
					)
				}} />

				<Route exact={true} path={`/${section_main_path}/advanced`} render={(route) => {
					if(!is_wl) window.location.hash = `/${section_main_path}`;

					const default_script_placeholder = '<script type="text/javascript">\n\tdocument.addEventListener("DOMContentLoaded", function() {\n\t\t/* '+i18n("Your code goes here")+'... */\n\t});\n</script>';

					return (
						<>
							<BuilderSidebar onSave={props.on_save}>
								<NavigationTabsBuilder
									active={route.location.pathname}
									sections={sections}
								>{props.stage_indicator}</NavigationTabsBuilder>

								<article>
									<Model model={props.promotion} change={changePromo}>
										<TextAreaPlaceHolder
											_class={{v_size:'huge', first:true}}
											name="code_tracking_roulette"
											zoomable={true}
											is_monospaced={true}
											placeholder={default_script_placeholder}
											label_props={{
												tooltip:<Tooltip>In this field you can insert HTML, Javascript or any other code that can be read by a web browser.</Tooltip>,
											}}
										>Javascript, HTML and your own code</TextAreaPlaceHolder>
									</Model>
								</article>
							</BuilderSidebar>

							<BuilderCanvas is_adaptable={false} fit_to_window="all" not_awesome={true}>
								<div class="not_awesome">
									<Message
										title="Custom code display"
										_class={{ other:'in_top' }}
									>
										<p>{i18n("Any change you apply here will only be visible in the Preview area of the promotion, not in Editor mode.")}</p>
									</Message>

									<p>{i18n("The code inserted here, will be added right before the closing BODY tag.")}</p>
									<ul class="bulleted_list">
										<li>{i18n("This code will be added only in this section pages.")} <FormattedMessage id="If you want to add a code that will need to be applied on every page of the promotion, you can use the {link}." values={{link:<ActionLink _href='#design/advanced'>Advanced Layout Section</ActionLink>}} /></li>
										<li>{i18n("jQuery compatible.")}</li>
										<li><ActionLink _href={window.zendesk_base_url + '207884626'} _target="_blank">See code examples and further information about custom code.</ActionLink></li>
									</ul>
								</div>
							</BuilderCanvas>
						</>
					)
				}} />
			</Switch>
		</BuilderMain>
	);
}

export default injectIntl(RouletteBuilder);